/**
 * Security Actions — a checklist of the security items identified while standing
 * SFP up. Completion state persists to Firestore at securityActions/state
 * (admin read/write per firestore.rules). The canonical action list lives here
 * in code so it's version-controlled; only the checked/unchecked state is stored.
 */
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';
import { useDoc } from '../../lib/hooks.js';

const DEMO_MODE = import.meta.env.VITE_SFP_DEMO_MODE === 'true';
const DEMO_KEY = 'sfp.securityActions.v1';

type Severity = 'critical' | 'high' | 'ongoing' | 'rollout';

interface SecurityAction {
  id: string;
  title: string;
  detail: string;
  severity: Severity;
}

/**
 * The list. CRITICAL = a live credential was exposed in setup chat and must be
 * rotated. HIGH = hardening that meaningfully reduces risk. ONGOING = recurring
 * hygiene.
 */
const ACTIONS: SecurityAction[] = [
  // ---- CRITICAL: rotate credentials exposed during setup --------------------
  {
    id: 'rotate-sa-key',
    severity: 'critical',
    title: 'Rotate the Firebase service-account key',
    detail:
      'The firebase-adminsdk service-account private key was shared in the setup chat and written to sfp/service-account.json. Firebase Console → Project settings → Service accounts → generate a new key, and delete the old key id (311844c470…). Then update the GMAIL_SA_JSON secret with the new key and redeploy pollBillsInbox.',
  },
  {
    id: 'delete-sa-file',
    severity: 'critical',
    title: 'Delete sfp/service-account.json from disk',
    detail:
      'Once the key above is rotated, delete the local file sfp/service-account.json. It is gitignored but should not sit on disk. (PowerShell: Remove-Item "…\\sfp\\service-account.json")',
  },
  {
    id: 'change-mailbox-pw',
    severity: 'critical',
    title: 'Change the sfpbills@shulkinfamilypartners.com mailbox password',
    detail:
      'The bills-inbox password was shared in chat. Change it in Google Workspace. Bill ingestion uses domain-wide delegation (a service account), not the password, so changing it will NOT break the poller.',
  },
  {
    id: 'change-admin-pw',
    severity: 'critical',
    title: 'Change the admin login password',
    detail:
      'jon@shulkinfamilypartners.com was bootstrapped with a temporary password that was shared in chat. Sign in and change it (or reset via Firebase Console → Authentication → Users).',
  },
  {
    id: 'rotate-anthropic',
    severity: 'critical',
    title: 'Rotate the Anthropic API key',
    detail:
      'ANTHROPIC_API_KEY was pasted in chat. Create a replacement at console.anthropic.com, revoke the old one, update the ANTHROPIC_API_KEY secret, and redeploy classifyDocument (firebase deploy --only functions:classifyDocument).',
  },
  {
    id: 'rotate-voyage',
    severity: 'critical',
    title: 'Rotate the Voyage API key',
    detail:
      'VOYAGE_API_KEY was pasted in chat. Regenerate at voyageai.com, update the VOYAGE_API_KEY secret, and redeploy embedDocument + searchDocuments.',
  },
  {
    id: 'rotate-dropbox',
    severity: 'critical',
    title: 'Rotate the Dropbox app secret',
    detail:
      'DROPBOX_APP_SECRET was pasted in chat. Regenerate it in the Dropbox App Console, update the DROPBOX_APP_SECRET secret, and redeploy the dropbox* functions. (Lower risk than the others, but still rotate.)',
  },

  // ---- HIGH: hardening ------------------------------------------------------
  {
    id: 'enable-2fa',
    severity: 'high',
    title: 'Enable 2-step verification on the owner Google account',
    detail:
      'The Google account that owns the shulkin-family-partners project (jshulkin@gmail.com) is the master key to everything. Turn on 2FA / a passkey at myaccount.google.com/security.',
  },
  {
    id: 'enable-gcip-allowlist',
    severity: 'high',
    title: 'Enable Identity Platform + deploy the signup allowlist',
    detail:
      'Upgrade Auth to Identity Platform (console.cloud.google.com/customer-identity) so the enforceAllowlist blocking trigger can deploy — it rejects any non-invited email at the Auth layer. Until then, access is gated only by the invite flow + role checks.',
  },
  {
    id: 'restrict-api-key',
    severity: 'high',
    title: 'Restrict the Firebase web API key',
    detail:
      'In GCP Console → APIs & Services → Credentials, add HTTP-referrer restrictions to the web API key so it only works from shulkinfamilypartners.com and *.web.app. (Firebase web keys are not secrets, but referrer-locking reduces abuse.)',
  },
  {
    id: 'app-check',
    severity: 'high',
    title: 'Enable Firebase App Check',
    detail:
      'Turn on App Check (reCAPTCHA Enterprise for web) to ensure only your real app — not scripts — can call Firestore, Storage, and callable functions.',
  },
  {
    id: 'billing-budget',
    severity: 'high',
    title: 'Set a GCP billing budget + alert',
    detail:
      'A runaway function or abuse could rack up cost on the Blaze plan. Set a monthly budget with email alerts at console.cloud.google.com/billing → Budgets & alerts.',
  },

  // ---- ONGOING: recurring hygiene ------------------------------------------
  {
    id: 'review-rules',
    severity: 'ongoing',
    title: 'Review Firestore + Storage security rules',
    detail:
      'Confirm least-privilege on every collection/path, and re-review whenever a new collection or module is added. Rules live in firestore.rules + storage.rules.',
  },
  {
    id: 'audit-cadence',
    severity: 'ongoing',
    title: 'Establish an audit-log review cadence',
    detail:
      'The audit/ collection records views, downloads, and admin actions. Review it on a schedule (e.g., monthly) for unexpected access — especially from outside accountants/lawyers.',
  },
  {
    id: 'access-review',
    severity: 'ongoing',
    title: 'Quarterly user-access review',
    detail:
      'Re-confirm every user’s role and category permissions still fit, and revoke anyone who no longer needs access (Admin → Users / revokeUser).',
  },

  // ---- TRADING ROLLOUT: finish wiring the Trading app -----------------------
  {
    id: 'trading-allowlist-gmail',
    severity: 'rollout',
    title: 'Allow jshulkin@gmail.com to sign in to SFP',
    detail:
      'The Trading desk is restricted to jshulkin@gmail.com and jon@shulkinfamilypartners.com. jon@ is already a super-admin, but jshulkin@gmail.com is off-domain and is blocked at signup. Add it to SUPER_ADMIN_EMAILS in functions/src/auth/beforeUserCreated.ts (or invite it via Admin → Invite), then deploy functions.',
  },
  {
    id: 'trading-url',
    severity: 'rollout',
    title: 'Set the Trading dashboard URL (VITE_TRADING_URL)',
    detail:
      'The Trading page opens the bot dashboard at VITE_TRADING_URL. Set it in sfp/apps/web/.env to the bot’s real server URL (behind TLS) before deploying. If unset it falls back to http://127.0.0.1:8787 (a bot running on your own machine).',
  },
  {
    id: 'trading-deploy',
    severity: 'rollout',
    title: 'Deploy SFP with the Trading module',
    detail:
      'After the two items above: firebase deploy --only hosting (from sfp/). This publishes the new Trading sidebar item, home tile, and gated page to the live site.',
  },
];

const SEVERITY_META: Record<Severity, { label: string; badge: string; ring: string }> = {
  critical: { label: 'Critical · rotate now', badge: 'bg-red-100 text-red-700', ring: 'border-red-200' },
  high: { label: 'High · hardening', badge: 'bg-amber-100 text-amber-800', ring: 'border-amber-200' },
  ongoing: { label: 'Ongoing · hygiene', badge: 'bg-stone-100 text-stone-600', ring: 'border-stone-200' },
  rollout: { label: 'Trading rollout', badge: 'bg-sky-100 text-sky-700', ring: 'border-sky-200' },
};

interface ItemState {
  done?: boolean;
  at?: number;
  by?: string;
}

export function SecurityActions() {
  const { user, role } = useAuth();
  const { data, loading } = useDoc<{ items?: Record<string, ItemState> }>(
    'securityActions',
    DEMO_MODE ? undefined : 'state',
  );

  // In demo mode, fall back to localStorage so the checklist still works offline.
  const demoItems: Record<string, ItemState> = DEMO_MODE
    ? JSON.parse(localStorage.getItem(DEMO_KEY) || '{}')
    : {};
  const items: Record<string, ItemState> = DEMO_MODE ? demoItems : data?.items ?? {};

  const isAdmin = role === 'admin';

  async function toggle(id: string, done: boolean) {
    const entry: ItemState = { done, at: Date.now(), by: user?.email ?? 'unknown' };
    if (DEMO_MODE) {
      const next = { ...demoItems, [id]: entry };
      localStorage.setItem(DEMO_KEY, JSON.stringify(next));
      // force re-render by writing then reloading state on next tick
      window.dispatchEvent(new Event('storage'));
      return;
    }
    await setDoc(doc(db, 'securityActions', 'state'), { items: { [id]: entry } }, { merge: true });
  }

  const total = ACTIONS.length;
  const doneCount = ACTIONS.filter((a) => items[a.id]?.done).length;
  const criticalOpen = ACTIONS.filter((a) => a.severity === 'critical' && !items[a.id]?.done).length;
  const pct = Math.round((doneCount / total) * 100);

  const groups: Severity[] = ['critical', 'high', 'rollout', 'ongoing'];

  return (
    <div className="mx-auto max-w-3xl px-6 py-8 space-y-6">
      <header>
        <div className="text-[10px] uppercase tracking-wider text-stone-400">Security</div>
        <h1 className="text-2xl font-semibold text-stone-800">Security Actions</h1>
        <p className="mt-1 text-sm text-stone-600">
          Everything that should be locked down after standing SFP up. Check items off as you
          complete them — progress is shared across admins.
        </p>
      </header>

      {/* progress */}
      <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between text-sm">
          <span className="font-semibold text-stone-800">
            {doneCount} / {total} complete
          </span>
          {criticalOpen > 0 ? (
            <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
              {criticalOpen} critical item{criticalOpen === 1 ? '' : 's'} still open
            </span>
          ) : (
            <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
              All critical items cleared
            </span>
          )}
        </div>
        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-stone-100">
          <div
            className="h-full rounded-full bg-stone-800 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {!isAdmin && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
          You can view this checklist, but only an admin can check items off.
        </div>
      )}

      {loading && !DEMO_MODE ? (
        <div className="text-sm text-stone-400">Loading…</div>
      ) : (
        groups.map((sev) => {
          const list = ACTIONS.filter((a) => a.severity === sev);
          const meta = SEVERITY_META[sev];
          return (
            <section key={sev} className={`rounded-2xl border bg-white p-5 shadow-sm ${meta.ring}`}>
              <div className="mb-3 flex items-center gap-2">
                <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${meta.badge}`}>
                  {meta.label}
                </span>
                <span className="text-[11px] text-stone-400">
                  {list.filter((a) => items[a.id]?.done).length}/{list.length}
                </span>
              </div>
              <ul className="space-y-3">
                {list.map((a) => {
                  const st = items[a.id];
                  const done = !!st?.done;
                  return (
                    <li key={a.id} className="flex gap-3">
                      <input
                        type="checkbox"
                        checked={done}
                        disabled={!isAdmin}
                        onChange={(e) => toggle(a.id, e.target.checked)}
                        className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-stone-800 disabled:cursor-not-allowed"
                      />
                      <div className="min-w-0">
                        <div
                          className={`text-sm font-medium ${
                            done ? 'text-stone-400 line-through' : 'text-stone-800'
                          }`}
                        >
                          {a.title}
                        </div>
                        <div className="mt-0.5 text-xs leading-relaxed text-stone-500">{a.detail}</div>
                        {done && st?.by && (
                          <div className="mt-1 text-[10px] uppercase tracking-wider text-stone-400">
                            Done by {st.by}
                            {st.at ? ` · ${new Date(st.at).toLocaleDateString()}` : ''}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })
      )}
    </div>
  );
}
